import { useEffect, useMemo, useState } from 'react';

import type { LanguageMode } from '~/components/Code';

type ViewMode = 'text' | 'markdown' | 'json';

/**
 * Checks if a string is valid JSON
 */
function isValidJSON(str: string): boolean {
  try {
    JSON.parse(str);
    return true;
  } catch {
    return false;
  }
}

/**
 * Formats JSON string with proper indentation
 */
function formatJSON(str: string): string {
  try {
    const parsed = JSON.parse(str);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return str;
  }
}

interface UseMessageViewModeProps {
  content: string;
}

interface UseMessageViewModeReturn {
  viewMode: ViewMode;
  displayContent: string;
  codeLanguage: LanguageMode;
  setViewMode: (mode: ViewMode) => void;
}

/**
 * Hook to manage message view mode (text/markdown/json) and content formatting
 */
export function useMessageViewMode({
  content
}: UseMessageViewModeProps): UseMessageViewModeReturn {
  // Determine default view mode based on content
  const defaultViewMode = useMemo(() => {
    if (isValidJSON(content)) {
      return 'json';
    }
    // Check if content looks like markdown (has markdown syntax)
    if (
      content.includes('#') ||
      content.includes('*') ||
      content.includes('`') ||
      content.includes('[') ||
      content.includes('![')
    ) {
      return 'markdown';
    }
    return 'text';
  }, [content]);

  const [viewMode, setViewMode] = useState<ViewMode>(defaultViewMode);

  // Update view mode when content changes
  useEffect(() => {
    setViewMode(defaultViewMode);
  }, [defaultViewMode]);

  const displayContent = useMemo(() => {
    if (viewMode === 'json' && isValidJSON(content)) {
      return formatJSON(content);
    }
    return content;
  }, [content, viewMode]);

  const codeLanguage: LanguageMode = viewMode === 'json' ? 'json' : 'markdown';

  return {
    viewMode,
    displayContent,
    codeLanguage,
    setViewMode
  };
}
